import {
  Base,
  ChevronLeftIcon,
  ReverCard,
  toast
} from '@itsrever/design-system'
import { useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LanguageSwitcher } from '@/ui/components/language-switcher'
import NewReverLogo from '@/assets/images/NewReverLogo.svg'
import {
  BrandInformation,
  WarehouseAddressForm,
  ReturnConditions,
  OnboardingShippingCosts,
  DashboardUsers
} from '@/ui/components/settings-components'
import { useSearchParams } from 'react-router'
import {
  OnboardingAction,
  OnboardingInfo,
  addressProblems,
  isValidBranding,
  isValidOnboardingUsers,
  isValidReturnConditions
} from '@/domain/onboarding'
import DialogProvider from '@/ui/components/dialog/dialog-provider'
import posthog from 'posthog-js'

const reducer = (
  state: OnboardingInfo,
  action: OnboardingAction
): OnboardingInfo => {
  switch (action.type) {
    case 'UPDATE_BRAND_INFORMATION':
      return { ...state, brandInformation: action.payload }
    case 'UPDATE_WAREHOUSE_ADDRESS':
      return { ...state, warehouseAddress: action.payload }
    case 'UPDATE_RETURN_CONDITIONS':
      return { ...state, returnConditions: action.payload }
    case 'UPDATE_DASHBOARD_USERS':
      return { ...state, dashboardUsers: action.payload }
    default:
      return state
  }
}

const slackWebhookURL = import.meta.env.VITE_SLACK_WEBHOOK_URL

const TOTAL_STEPS = 3

// Outside Layout, so it mounts its own provider: the shipping rules confirm through useDialog.
export default function Onboarding() {
  return (
    <DialogProvider>
      <OnboardingSteps />
    </DialogProvider>
  )
}

function OnboardingSteps() {
  const { t } = useTranslation()
  const [params] = useSearchParams()
  const group = params.get('group')

  const [onboardingStep, setOnboardingStep] = useState(0)

  const initialState: OnboardingInfo = {
    brandInformation: {
      ecommerceName: '',
      group: group ?? '',
      platform: undefined,
      shopifyCollaboratorAccess: '',
      tiktokShopId: '',
      restock: undefined,
      contractsToUse: undefined
    },
    warehouseAddress: {
      email: '',
      name: '',
      company: '',
      phone: '',
      country: '',
      countryCode: '',
      province: '',
      city: '',
      postcode: '',
      addressLine1: '',
      addressLine2: ''
    },
    returnConditions: {
      returnDays: null,
      refundMethods: [],
      timing: 'NO_TIMING',
      returnMethods: [],
      fallbackBankTransfer: null,
      returnsPolicy: null
    },
    dashboardUsers: {
      users: []
    }
  }

  const [formInfo, dispatch] = useReducer(reducer, initialState)

  const handleClickNext = () => {
    setOnboardingStep((prev) => prev + 1)
    if (onboardingStep === 0) {
      posthog?.capture('Onboarding - First step completed')
    } else if (onboardingStep === 1) {
      posthog?.capture('Onboarding - Second step completed')
    }
  }

  const handleSubmit = async () => {
    posthog?.capture('Onboarding - Completed')
    const ecommerceId = calcEcommerceId(formInfo.brandInformation.ecommerceName)

    const searchParamsObject = Object.fromEntries(params.entries())

    const payload = {
      text: `New onboarding request by \`${ecommerceId}\` with the following information:\n\`\`\`${JSON.stringify(
        {
          ...formInfo,
          urlParams: searchParamsObject
        },
        null,
        2
      )}\`\`\``
    }
    fetch(slackWebhookURL, {
      method: 'post',
      body: JSON.stringify(payload)
    })
      .catch((error) => console.warn(error))
      .then(() => {
        toast({
          text: t('onboarding.success'),
          variant: 'success'
        })
        setOnboardingStep((prev) => prev + 1)
      })
  }

  const disabledNext =
    !isValidBranding(formInfo.brandInformation) ||
    Object.keys(addressProblems(formInfo.warehouseAddress)).length > 0 ||
    !isValidReturnConditions({
      ...formInfo.returnConditions,
      returnsPolicy: ''
    })

  function calcEcommerceId(name: string) {
    return name.toLowerCase().replace(/\s+/g, '')
  }

  const isShopify =
    formInfo.brandInformation.platform === 'Shopify' ||
    formInfo.brandInformation.platform === 'Shopify Plus'

  const handleClickBack = () => {
    setOnboardingStep((prev) => prev - 1)
  }

  const disableNextUser = !isValidOnboardingUsers(formInfo.dashboardUsers.users)

  return (
    <div className="flex h-full flex-col items-center overflow-scroll bg-grey-6">
      <div className="flex h-[64px] w-full items-center justify-between border-b border-solid border-neutral-200 bg-white p-4">
        <div className="flex items-center gap-4">
          <img src={NewReverLogo} alt="Rever" className="h-14" />
          <h6>
            {onboardingStep === 0
              ? t('onboarding.define_brand_information')
              : onboardingStep === 1
                ? t('onboarding.define_users')
                : t('onboarding.define_return_shipping_cost')}
          </h6>
        </div>
        <LanguageSwitcher />
      </div>
      {onboardingStep === 0 ? (
        <div className="p-4">
          <ReverCard className="max-w-[1250px] p-4">
            <div className="mb-4 flex">
              <h6>ONBOARDING</h6>
              <span className="ml-auto">
                {onboardingStep + 1} / {TOTAL_STEPS}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <BrandInformation
                brandInformation={formInfo.brandInformation}
                dispatch={dispatch}
              />
              <WarehouseAddressForm
                warehouseAddress={formInfo.warehouseAddress}
                dispatch={dispatch}
              />
              <ReturnConditions
                returnConditions={formInfo.returnConditions}
                dispatch={dispatch}
                isShopify={isShopify}
              />
            </div>

            <div className="mt-4 flex justify-between gap-4">
              <div />
              <Base.Button
                type="button"
                variant="primary"
                onClick={handleClickNext}
                disabled={disabledNext}
              >
                {t('onboarding.next')}
              </Base.Button>
            </div>
          </ReverCard>
        </div>
      ) : onboardingStep === 1 ? (
        <div className="w-[1200px] p-4">
          <ReverCard className="p-4" fullWidth>
            <div className="mb-4 flex w-full items-center justify-between px-4">
              <div className="flex cursor-pointer items-center gap-2 rounded-md p-1 hover:bg-grey-4">
                <ChevronLeftIcon />
                <p onClick={handleClickBack}>{t('item_details.back')}</p>
              </div>
              <p>
                {onboardingStep + 1} / {TOTAL_STEPS}
              </p>
            </div>
            <DashboardUsers
              dashboardUsers={formInfo.dashboardUsers}
              dispatch={dispatch}
            />
            <div className="mt-4 flex justify-between gap-4">
              <div />
              <Base.Button
                type="button"
                variant="primary"
                onClick={handleClickNext}
                disabled={disableNextUser}
              >
                {t('onboarding.next')}
              </Base.Button>
            </div>
          </ReverCard>
        </div>
      ) : onboardingStep === 2 ? (
        <div className="w-[1000px] p-4">
          <ReverCard className="p-4" fullWidth>
            <div className="flex w-full items-center justify-between self-start px-4 pt-4">
              <div className="flex cursor-pointer items-center gap-2 rounded-md p-1 hover:bg-grey-4">
                <ChevronLeftIcon />
                <p onClick={handleClickBack}>{t('item_details.back')}</p>
              </div>
              <p>
                {onboardingStep + 1} / {TOTAL_STEPS}
              </p>
            </div>
            <div className="mb-4 space-y-2 px-8 pt-4 ">
              <h6>{t('onboarding.shipping_costs')}</h6>
              <p className="max-w-2xl pb-4 text-sm font-medium text-neutral-600">
                {t('onboarding.shipping_costs_description')}
              </p>
            </div>
            <OnboardingShippingCosts
              ecommerceId={calcEcommerceId(
                formInfo.brandInformation.ecommerceName
              )}
              onSave={handleSubmit}
              onboardingInfo={formInfo}
              urlParams={Object.fromEntries(params.entries())}
            />
          </ReverCard>
        </div>
      ) : (
        <div className="mt-6">
          <ReverCard className="p-4">
            <h6>Thank you!</h6>
            <p className="text-success-main">
              {t('onboarding.success_description')}
            </p>
          </ReverCard>
        </div>
      )}
    </div>
  )
}
